import type {
  ChargeItemSummaryDto,
  PriceDto,
} from '@gm-commercial/entitlements-client/src/api/entitlementsApi';
import type { AddonProduct } from '~/features/catalog/addons/addonCatalog.types';
import { PaymentProperties } from './billing.model';
import type { HealthData } from './peripheral.model';
import type {
  BillingCycleDuration,
  EligibilityAssetType,
  PlanLabel,
  ProductCodes,
} from './plans.model';

export type {
  ACHPaymentMethodInfo,
  CreditCardPaymentMethodInfo as PaymentMethodInfo,
  GmFinancialPaymentMethodInfo,
  InvoiceMePaymentMethodInfo,
  PaymentBadge,
  PaymentMethodsForCheckout,
  RpoPrepaidPaymentMethodInfo,
  ValidCreditCardType,
} from './billing.model';
// Re-export billing types — prefer importing directly from ./billing.model in new code.
export { CreditCardType, PaymentProperties } from './billing.model';

export enum EnrollOptions {
  ALL = 'all',
  INDIVIDUAL = 'individual',
}

export interface FeatureDetailsCategory {
  category: string;
  features: string[];
}

export enum ActiveTab {
  Choose_Plan = 'Choose_Plan',
  Enter_Billing = 'Enter_Billing',
  Summary = 'Summary',
}

export interface FeatureEntity {
  id: string;
  titleKey: string;
  descriptionKey: string;
  selected?: boolean;
  image: string;
  tooltip?: string;
  tooltipKey?: string;
}

export interface OrderSummaryDetails {
  isProrated: boolean;
  taxAmount: number;
  subtotalPrice: number;
  totalPrice: number;
  invoiceStartDate: string;
  invoiceEndDate: string;
  nextInvoiceStartDate: string;
  nextInvoiceTotalPrice: number;
  nextInvoiceSubtotalPrice: number;
  chargeItems: ChargeItemSummaryDto[];
  discountAmount?: number;
  /**
   * TODO: persist-only collapse of the first charge line with a billed unit.
   * Leaves still use list × percent. `chargeItems` already has per-line
   * `unitPrice` and `discountedUnitPrice`; ABP display should map those onto
   * each PricingGroup and drop this field.
   */
  discountedUnitPrice?: number;
  discountPercent?: number;
  discountReasonCode?: string;
}

export type VehicleTable = {
  id: string;
  vin: string;
  asset: string;
  fleet: string;
  vehicleTypeDetail: string;
  imageUrl: string;
  plans: PlanLabel[];
};

export type VehicleDetails = {
  vin: string;
  vehicleTypeDetail: string;
  imageUrl: string;
};

export type PeripheralDetails = {
  make?: string;
  model?: string;
  serialNumber?: string;
  firmware?: string;
  imei?: string;
  peripheralType?: string;
  health?: HealthData;
  lastCommunicatedDateTime?: string;
};

/**
 * Unified table row covering both vehicles and peripheral devices.
 * Use `assetType` as the discriminator:
 * - `vehicle` rows populate `vehicleDetails`
 * - `peripheral` rows populate `peripheralDetails`
 */
export type AssetTable = {
  id: string;
  assetType: EligibilityAssetType;
  asset: string;
  fleet: string;
  plans?: PlanLabel[];
  pricingInformation?: PriceDto;
  vehicleDetails?: VehicleDetails;
  peripheralDetails?: PeripheralDetails;
};

export const EnrollmentView = {
  PLAN_ORDER_CHECKOUT: 'plan-order-checkout',
  DEVICE_ORDER_CHECKOUT: 'device-order-checkout',
  PLAN_MANAGEMENT: 'plan-management',
  PREPAID_VEHICLE_LIST: 'prepaid-vehicle-list',
} as const;

export type EnrollmentViewType =
  (typeof EnrollmentView)[keyof typeof EnrollmentView];

export interface EditPaymentFormData {
  [PaymentProperties.maskedCreditCardNumber]: string;
  [PaymentProperties.expirationDate]: string;
  [PaymentProperties.holderName]: string;
  [PaymentProperties.addressLine1]: string;
  [PaymentProperties.addressLine2]: string;
  [PaymentProperties.country]: string;
  [PaymentProperties.city]: string;
  [PaymentProperties.state]: string;
  [PaymentProperties.postalCode]: string;
}

export interface EditPaymentFormFields extends EditPaymentFormData {
  [PaymentProperties.securityCode]: string;
}

export interface PlanModalFeature {
  key: string;
  titleKey: string;
  summaryKey: string;
  imageUrl: string;
  tooltipKey?: string;
  tooltipAriaLabelKey?: string;
}

export interface GuideLinkInfo {
  url: string;
  textKey: string;
}

export interface PlanModalData {
  subtitleKey: string;
  heroMediaUrl: string;
  features: PlanModalFeature[];
  videoUrl?: string;
  descriptionKey?: string;
  guideLinkInfo?: GuideLinkInfo;
}

export type ModalAssets = Record<
  ProductCodes,
  {
    hero: string;
    [key: string]: string;
  }
>;

export type ProductModalState = {
  productCode: ProductCodes;
  billingCycleDuration?: BillingCycleDuration;
  eligibleVehicles?: number;
} | null;

export type AddonProductModalState = {
  addonProduct: AddonProduct;
  selectedBillingCycle?: BillingCycleDuration;
} | null;
